/**
 * VesselSettingsHistoryView
 *
 * Audit-trail viewer for vessel settings changes. Unlike the equipment
 * ChangeLogViewer (which shows add/edit/delete actions on individual items),
 * vessel settings audit is fundamentally about field-level edits — what
 * field changed from what value to what value, by whom and when. So this
 * viewer renders a per-entry diff table.
 *
 * Reads from the same useAuditTrail storage (module name 'vessel_settings'),
 * which after today's key-mismatch fix lives at the org-scoped key
 * audit_log_vessel_settings_<orgId>_<vesselId>.
 */

import React, { useMemo } from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuditTrail } from '../../../hooks/useAuditTrail';
import { labelForVesselSettingsField, isNonDisplayVesselSettingsField } from './vesselSettingsAuditFields';

// FT-515 round 7: field labels + the internal-field exclusion rule now live
// in a shared module (vesselSettingsAuditFields.js) so write-time
// (VesselSettingsView.js's diff) and display-time (here) agree on what
// counts as a real, user-meaningful vessel-settings field. See that module's
// header comment for the full rationale — in short, this view used to render
// EVERY key the before/after diff produced verbatim, including internal
// bookkeeping fields (`vesselId`, `access_level`, `_sourceModule`,
// `_needsCloudCheck`, …) that the diff picked up because the "before"
// snapshot was the full live vessel object while "after" was only the
// schema-mapped form fields. labelFor keeps the "fall back to raw key, never
// hide an unrecognised real field" behaviour from before — only fields
// isNonDisplayVesselSettingsField positively flags as internal are dropped.
const labelFor = (key) => labelForVesselSettingsField(key);

// Render a value for audit display. Primitives shown verbatim, arrays/objects
// JSON-stringified compactly. Long values truncated for UI compactness.
const renderValue = (v) => {
  if (v === null || v === undefined) return <span className="text-gray-400 italic">(empty)</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  } catch (_e) {
    return String(v);
  }
};

const VesselSettingsHistoryView = ({ onBack }) => {
  // FT-515 round 2: getChanges() now reads from useAuditTrail's async-hydrated
  // `entries` state (see useAuditTrail.js), not a synchronous storage read.
  // getChanges is a useCallback whose identity changes when that state
  // resolves, so depending on it here (instead of an empty-deps useMemo) is
  // what makes this view actually re-derive once the load finishes — an
  // empty-deps memo would freeze on the pre-hydration `[]` forever. isLoading
  // is exposed the same way ChangeLogViewer/RAHistoryViewer use it, so this
  // view shows a loading state instead of a premature "no changes" empty
  // state before the read resolves.
  const { logChange: _logChange, getChanges, isLoading } = useAuditTrail('vessel_settings');
  const entries = useMemo(() => {
    try {
      const all = getChanges?.() || [];
      // Newest first
      return [...all].sort((a, b) => {
        const ta = new Date(a.timestamp || 0).getTime();
        const tb = new Date(b.timestamp || 0).getTime();
        return tb - ta;
      });
    } catch (e) {
      console.warn('[VesselSettingsHistoryView] Failed to load audit log:', e);
      return [];
    }
  }, [getChanges]);

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            title="Back to vessel settings"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Vessel Settings History</h3>
            <p className="text-sm text-gray-500">
              {isLoading ? 'Loading…' : `${entries.length} change${entries.length === 1 ? '' : 's'} recorded`}
            </p>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="px-6 py-5">
        {isLoading ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Loading vessel settings history…</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No vessel settings changes recorded yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Edits to vessel settings will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Change</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Field</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Before</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">After</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Timestamp</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {entries.map((entry, idx) => {
                  const changedFields = entry.changes?.changedFields || {};
                  // FT-515 round 7: strip internal/bookkeeping fields before
                  // rendering — this is the defense-in-depth half of the fix
                  // (also applies to entries already written to storage
                  // before this round, including by the old, unfiltered
                  // write path). See vesselSettingsAuditFields.js.
                  const fieldEntries = Object.entries(changedFields)
                    .filter(([key]) => !isNonDisplayVesselSettingsField(key));
                  // Render one row per changed field; if no field diff (or
                  // every changed field was internal-only noise), render one
                  // summary row
                  if (fieldEntries.length === 0) {
                    return (
                      <tr key={entry.id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Updated
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500" colSpan={2}>—</td>
                        <td className="px-4 py-3 text-sm text-gray-500"></td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {entry.user?.name || 'Unknown'}
                          {entry.user?.role ? ` (${entry.user.role})` : ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {entry.timestampLocal || entry.timestamp || '—'}
                        </td>
                      </tr>
                    );
                  }
                  return fieldEntries.map(([key, { from, to }], fieldIdx) => (
                    <tr key={`${entry.id || idx}-${key}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {fieldIdx === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Updated
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                        {labelFor(key)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {renderValue(from)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                        {renderValue(to)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {fieldIdx === 0 ? (
                          <>
                            {entry.user?.name || 'Unknown'}
                            {entry.user?.role ? ` (${entry.user.role})` : ''}
                          </>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {fieldIdx === 0 ? (entry.timestampLocal || entry.timestamp || '—') : null}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselSettingsHistoryView;
